import { useState, useRef, useLayoutEffect, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ChevronRight, Play, RefreshCw, Database,
  Table2, Layers, Package, Link2, Settings2, Square, Trash2,
} from 'lucide-react'
import { getFunnelConnector, getFunnelRuns, startFunnelRun, stopFunnelRun, deleteFunnelRun, getFunnelRunLogs, deleteFunnelConnector } from '../api/funnel'
import { useDateFmt } from '../hooks/useDateFmt'
import { Modal } from '../components/Modal'
import { Button } from '../components/ui/button'
import type { FunnelRun, FunnelRunLog, FunnelTable, FunnelConnector } from '../types'

type RunState = {
  isRunning: boolean
  done: number
  total: number
  pct: number
  activeTable: string | null
  phase: string | null
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtDuration(run: FunnelRun): string {
  if (!run.completedAt) return run.status === 'running' ? 'running…' : '—'
  const s = Math.round((new Date(run.completedAt).getTime() - new Date(run.startedAt).getTime()) / 1000)
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`
}

function durationSecs(run: FunnelRun): number {
  if (!run.completedAt) return 0
  return (new Date(run.completedAt).getTime() - new Date(run.startedAt).getTime()) / 1000
}

function bezier(from: { x: number; y: number }, to: { x: number; y: number }) {
  const cx = (from.x + to.x) / 2
  return `M ${from.x} ${from.y} C ${cx} ${from.y} ${cx} ${to.y} ${to.x} ${to.y}`
}

function edgePoints(el: HTMLElement, container: HTMLElement) {
  const er = el.getBoundingClientRect()
  const cr = container.getBoundingClientRect()
  const cy = er.top + er.height / 2 - cr.top
  return {
    left:  { x: er.left  - cr.left, y: cy },
    right: { x: er.right - cr.left, y: cy },
  }
}

// ── Status badge ──────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: FunnelRun['status'] }) {
  const styles = {
    running:   'bg-blue-50 text-blue-600 border-blue-100',
    completed: 'bg-emerald-50 text-emerald-600 border-emerald-100',
    failed:    'bg-red-50 text-red-600 border-red-100',
  }
  return (
    <span className={`px-1.5 py-0.5 text-theme-xs font-medium rounded border ${styles[status]}`}>
      {status}
    </span>
  )
}

// ── Logs modal ────────────────────────────────────────────────────────────────

function LogsModal({ runId, onClose }: { runId: string; onClose: () => void }) {
  const { data: logs = [] } = useQuery({
    queryKey: ['funnel-run-logs', runId],
    queryFn: () => getFunnelRunLogs(runId).then(r => r.data),
    refetchInterval: 3000,
  })
  const levelColor = { info: 'text-ink-dim', warn: 'text-amber-600', error: 'text-red-600' }
  return (
    <Modal title="Run logs" onClose={onClose} size="lg">
      <div className="font-mono text-theme-xs space-y-1 max-h-96 overflow-y-auto">
        {(logs as FunnelRunLog[]).length === 0 ? (
          <p className="text-ink-dim text-center py-4">No logs yet</p>
        ) : (logs as FunnelRunLog[]).map(log => (
          <div key={log.id} className={levelColor[log.level]}>
            <div className="flex gap-2">
              <span className="text-ink-dim shrink-0">{new Date(log.createdAt).toLocaleTimeString()}</span>
              <span className="uppercase shrink-0 w-8">{log.level}</span>
              <span className="flex-1">{log.message}</span>
            </div>
            {log.details && (
              <details className="ml-[88px] mt-0.5">
                <summary className="text-ink-dim cursor-pointer select-none">details</summary>
                <pre className="text-ink-dim whitespace-pre-wrap break-all bg-paper-sunken border border-paper-border rounded p-2 mt-1">
                  {JSON.stringify(log.details, null, 2)}
                </pre>
              </details>
            )}
          </div>
        ))}
      </div>
    </Modal>
  )
}

// ── Runs bar chart ────────────────────────────────────────────────────────────

const CHART_H = 156
const LABEL_H = 28
const YAXIS_W = 36
const BAR_MIN_H = 3

function RunsChart({ runs }: { runs: FunnelRun[] }) {
  const fmt = useDateFmt()
  const wrapRef = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(0)
  const [hovered, setHovered] = useState<number | null>(null)

  useLayoutEffect(() => {
    const measure = () => { if (wrapRef.current) setWidth(wrapRef.current.offsetWidth) }
    measure()
    const ro = new ResizeObserver(measure)
    if (wrapRef.current) ro.observe(wrapRef.current)
    return () => ro.disconnect()
  }, [])

  const recent = useMemo(() => [...runs].reverse().slice(-40), [runs])
  const durations = useMemo(() => recent.map(durationSecs), [recent])
  const maxDur = Math.max(...durations, 1)

  const n = recent.length
  const innerW = width - YAXIS_W
  const barW = n === 0 ? 0 : Math.max(6, Math.floor((innerW - (n - 1) * 3) / n))
  const gap   = n <= 1 ? 0 : (innerW - n * barW) / Math.max(n - 1, 1)

  const barColor = (r: FunnelRun) => {
    if (r.status === 'failed')  return '#f04438'
    if (r.status === 'running') return '#465fff'
    return '#12b76a'
  }

  const yTicks = [0, Math.round(maxDur / 2), Math.round(maxDur)]

  if (width === 0) return <div ref={wrapRef} style={{ height: CHART_H + LABEL_H }} />

  return (
    <div ref={wrapRef} className="select-none">
      <svg width={width} height={CHART_H + LABEL_H} className="overflow-visible">

        {/* Y grid + labels */}
        {yTicks.map((v, i) => {
          const y = CHART_H - Math.max(0, (v / maxDur) * (CHART_H - BAR_MIN_H))
          return (
            <g key={i}>
              <line x1={YAXIS_W} y1={y} x2={width} y2={y} stroke="#f2f4f7" strokeWidth={1} />
              {v > 0 && (
                <text x={YAXIS_W - 5} y={y + 4} textAnchor="end" fontSize={9} fill="#98a2b3">
                  {v >= 60 ? `${Math.floor(v / 60)}m` : `${v}s`}
                </text>
              )}
            </g>
          )
        })}

        {/* Bars */}
        {recent.map((run, i) => {
          const dur   = durations[i]
          const barH  = Math.max(BAR_MIN_H, (dur / maxDur) * (CHART_H - BAR_MIN_H))
          const x     = YAXIS_W + i * (barW + gap)
          const y     = CHART_H - barH
          const isHov = hovered === i

          // Tooltip positioning: keep within chart bounds
          const ttW  = 130
          const ttH  = 46
          const ttX  = Math.min(Math.max(x - ttW / 2 + barW / 2, YAXIS_W), width - ttW)
          const ttY  = Math.max(0, y - ttH - 6)

          return (
            <g key={run.id}
               onMouseEnter={() => setHovered(i)}
               onMouseLeave={() => setHovered(null)}
               style={{ cursor: 'default' }}>
              <rect
                x={x} y={y} width={barW} height={barH} rx={2}
                fill={barColor(run)}
                opacity={isHov ? 1 : 0.72}
              />
              {isHov && (
                <g>
                  <rect x={ttX} y={ttY} width={ttW} height={ttH} rx={4} fill="#1d2939" />
                  <text x={ttX + 8} y={ttY + 14} fontSize={10} fill="white" fontWeight="600">
                    {fmtDuration(run)}
                  </text>
                  <text x={ttX + 8} y={ttY + 27} fontSize={9} fill="#98a2b3">
                    {fmt(run.startedAt)}
                  </text>
                  <text x={ttX + 8} y={ttY + 39} fontSize={9} fill="#98a2b3">
                    +{run.rowsCreated.toLocaleString()} created · ~{run.rowsUpdated.toLocaleString()} updated
                    {run.rowsFailed > 0 ? ` · ${run.rowsFailed.toLocaleString()} failed` : ''}
                  </text>
                </g>
              )}
            </g>
          )
        })}

        {/* X axis labels (sparse) */}
        {recent.map((run, i) => {
          const show = n <= 12 || i % Math.ceil(n / 10) === 0 || i === n - 1
          if (!show) return null
          const x = YAXIS_W + i * (barW + gap) + barW / 2
          return (
            <text key={run.id} x={x} y={CHART_H + LABEL_H - 4}
                  textAnchor="middle" fontSize={9} fill="#98a2b3">
              {new Date(run.startedAt).toLocaleTimeString('en-US', {
                hour: '2-digit', minute: '2-digit', hour12: false,
              })}
            </text>
          )
        })}
      </svg>

      {/* Legend */}
      <div className="flex items-center gap-4 mt-1">
        {[
          { color: '#12b76a', label: 'Completed' },
          { color: '#f04438', label: 'Failed' },
          { color: '#465fff', label: 'Running' },
        ].map(({ color, label }) => (
          <span key={label} className="flex items-center gap-1 text-[10px] text-ink-dim">
            <span className="w-2 h-2 rounded-sm inline-block" style={{ background: color }} />
            {label}
          </span>
        ))}
      </div>
    </div>
  )
}

// ── Progress ring ─────────────────────────────────────────────────────────────

function ProgressRing({ pct }: { pct: number }) {
  const r = 24
  const circ = 2 * Math.PI * r
  const offset = circ * (1 - Math.min(pct, 100) / 100)
  return (
    <svg width={56} height={56} style={{ transform: 'rotate(-90deg)' }}>
      <circle cx={28} cy={28} r={r} fill="none" stroke="#e4e7ec" strokeWidth={5} />
      <circle cx={28} cy={28} r={r} fill="none" stroke="#465fff" strokeWidth={5}
        strokeDasharray={`${circ}`} strokeDashoffset={offset}
        strokeLinecap="round"
        style={{ transition: 'stroke-dashoffset 0.5s ease' }}
      />
      <text x={28} y={32} textAnchor="middle" fontSize={11} fontWeight="bold" fill="#1d2939"
            style={{ transform: 'rotate(90deg)', transformOrigin: '28px 28px' }}>
        {Math.round(pct)}%
      </text>
    </svg>
  )
}

// ── Pipeline graph ────────────────────────────────────────────────────────────

type Conn = { d: string; color: string; dashed?: boolean; from: string; to: string }
type ConnectorWithTables = FunnelConnector & { tables: FunnelTable[] }

function PipelineGraph({ connector, runState }: { connector: ConnectorWithTables; runState: RunState }) {
  const containerRef = useRef<HTMLDivElement>(null)
  const sourceRef    = useRef<HTMLDivElement>(null)
  const tableRefs    = useRef<Map<string, HTMLDivElement>>(new Map())
  const cupRefs      = useRef<Map<string, HTMLDivElement>>(new Map())
  const relRefs      = useRef<Map<string, HTMLDivElement>>(new Map())

  const [conns,       setConns]       = useState<Conn[]>([])
  const [svgSize,     setSvgSize]     = useState({ w: 0, h: 0 })
  const [selectedKey, setSelectedKey] = useState<string | null>(null)

  const tables = connector.tables ?? []

  const cups = useMemo(() => {
    const seen = new Set<string>()
    const result: Array<{ key: string; id: string | null; name: string }> = []
    for (const t of tables) {
      if (t.tableType !== 'data') continue
      const name = t.cupName || t.tableName
      const key  = t.cupId ?? `name:${name}`
      if (seen.has(key)) continue
      seen.add(key)
      result.push({ key, id: t.cupId, name })
    }
    return result
  }, [tables])

  const relTables = useMemo(() => tables.filter(t => t.tableType === 'relation'), [tables])

  useLayoutEffect(() => {
    function recompute() {
      const ctr = containerRef.current
      if (!ctr) return
      const cr = ctr.getBoundingClientRect()
      setSvgSize({ w: cr.width, h: cr.height })

      const next: Conn[] = []

      const srcEl = sourceRef.current
      if (srcEl) {
        const sp = edgePoints(srcEl, ctr).right
        for (const [tid, tEl] of tableRefs.current) {
          next.push({ d: bezier(sp, edgePoints(tEl, ctr).left), color: '#d0d5dd', from: '__source__', to: tid })
        }
      }

      for (const t of tables) {
        const tEl = tableRefs.current.get(t.id)
        if (!tEl) continue

        if (t.tableType === 'data') {
          const cupKey = t.cupId ?? `name:${t.cupName || t.tableName}`
          const cEl = cupRefs.current.get(cupKey)
          if (cEl) next.push({ d: bezier(edgePoints(tEl, ctr).right, edgePoints(cEl, ctr).left), color: '#12b76a', from: t.id, to: cupKey })
        }

        if (t.tableType === 'relation') {
          const rEl = relRefs.current.get(t.id)
          if (rEl) {
            next.push({ d: bezier(edgePoints(tEl, ctr).right, edgePoints(rEl, ctr).left), color: '#a855f7', dashed: true, from: t.id, to: `reldst:${t.id}` })
          }
          if (t.links) {
            for (const link of t.links) {
              const linked = tables.find(x => x.tableName === link.linkedTableName)
              if (linked) {
                const lEl = tableRefs.current.get(linked.id)
                if (lEl) next.push({ d: bezier(edgePoints(tEl, ctr).right, edgePoints(lEl, ctr).left), color: '#a855f7', dashed: true, from: t.id, to: linked.id })
              }
            }
          }
        }
      }

      setConns(next)
    }

    recompute()
    const ro = new ResizeObserver(recompute)
    if (containerRef.current) ro.observe(containerRef.current)
    return () => ro.disconnect()
  }, [tables, cups, relTables])

  const toggle = (key: string) => setSelectedKey(p => p === key ? null : key)

  const highlightedNodes = useMemo<Set<string> | null>(() => {
    if (!selectedKey) return null
    const set = new Set<string>([selectedKey])
    for (const c of conns) {
      if (c.from === selectedKey || c.to === selectedKey) {
        set.add(c.from)
        set.add(c.to)
      }
    }
    return set
  }, [selectedKey, conns])

  const nodeRing = (key: string) => {
    if (!highlightedNodes) return ''
    if (key === selectedKey) return 'ring-2 ring-accent ring-offset-1'
    if (highlightedNodes.has(key)) return 'ring-1 ring-accent/40 ring-offset-1'
    return 'opacity-30'
  }

  const { isRunning, activeTable, phase, done, total, pct } = runState

  const edgeStyle = (c: Conn) => {
    if (isRunning) return { opacity: 0.7, width: 1.5 }
    if (!highlightedNodes) return { opacity: 0.65, width: 1.5 }
    const active = highlightedNodes.has(c.from) && highlightedNodes.has(c.to)
    return { opacity: active ? 1 : 0.06, width: active ? 2 : 1.5 }
  }

  const nodeBase = 'bg-white border rounded-lg px-3 py-2.5 shadow-sm relative z-10 cursor-pointer select-none transition-all'

  return (
    <div
      ref={containerRef}
      className="relative flex items-start gap-10 p-8 overflow-auto min-h-[320px] w-full"
      onClick={e => { if (e.target === e.currentTarget) setSelectedKey(null) }}
    >
      {isRunning && (
        <style>{`
          @keyframes funnel-flow { to { stroke-dashoffset: -18; } }
          .funnel-flowing { animation: funnel-flow 0.5s linear infinite; }
        `}</style>
      )}

      {/* SVG connection overlay */}
      <svg className="absolute inset-0 pointer-events-none z-0" width={svgSize.w} height={svgSize.h}>
        {conns.map((c, i) => {
          const { opacity, width } = edgeStyle(c)
          return (
            <path key={i} d={c.d} fill="none" stroke={isRunning ? '#465fff' : c.color}
                  strokeWidth={isRunning ? 2 : width}
                  strokeDasharray={isRunning ? '10 8' : c.dashed ? '5 3' : undefined}
                  className={isRunning ? 'funnel-flowing' : undefined}
                  opacity={opacity} />
          )
        })}
      </svg>

      {/* Progress overlay */}
      {isRunning && (
        <div className="absolute top-4 right-6 z-20 bg-white/95 backdrop-blur-sm border border-accent/20 rounded-xl shadow-lg px-4 py-3 flex items-center gap-3">
          <ProgressRing pct={pct} />
          <div>
            <div className="text-sm font-semibold text-ink">{done}/{total} tables</div>
            <div className="text-[10px] text-accent font-mono mt-0.5 max-w-[140px] truncate">
              {activeTable ? `⟳ ${activeTable}` : phase ?? 'Preparing…'}
            </div>
          </div>
        </div>
      )}

      {/* Source column */}
      <div className="flex-none flex flex-col gap-2 min-w-[170px]">
        <div className="text-[10px] font-semibold text-ink-dim uppercase tracking-widest mb-1">Source</div>
        <div
          ref={sourceRef}
          className={`${nodeBase} border-paper-border min-w-[160px] ${nodeRing('__source__')}`}
          onClick={() => toggle('__source__')}
        >
          <div className="flex items-center gap-2 mb-1">
            <Database size={13} className="text-ink-dim shrink-0" />
            <span className="text-sm font-semibold text-ink truncate">{connector.databaseName}</span>
          </div>
          <div className="font-mono text-[10px] text-ink-dim truncate">{connector.host}:{connector.port}</div>
          <div className="text-[10px] text-ink-dim mt-0.5 uppercase tracking-wide">{connector.connectorType}</div>
        </div>
      </div>

      {/* Tables column */}
      <div className="flex flex-col gap-3 flex-1 min-w-[200px]">
        <div className="text-[10px] font-semibold text-ink-dim uppercase tracking-widest mb-1">Tables</div>
        {tables.length === 0 ? (
          <div className="text-sm text-ink-dim border border-dashed border-paper-border rounded-lg p-6 text-center">
            No tables configured
          </div>
        ) : tables.map(t => {
          const isActive = isRunning && activeTable === t.tableName
          return (
          <div
            key={t.id}
            ref={el => { if (el) tableRefs.current.set(t.id, el); else tableRefs.current.delete(t.id) }}
            className={`${nodeBase} ${
              isActive ? 'border-blue-400 ring-2 ring-blue-300 ring-offset-1 shadow-blue-100 shadow-md'
                : t.tableType === 'relation' ? 'border-purple-300' : 'border-paper-border'
            } ${nodeRing(t.id)}`}
            onClick={() => toggle(t.id)}
          >
            <div className="flex items-center gap-1.5">
              {t.tableType === 'data'
                ? <Table2 size={11} className="text-ink-dim shrink-0" />
                : <Layers  size={11} className="text-purple-400 shrink-0" />}
              <span className="font-mono text-sm font-medium text-ink truncate flex-1">{t.tableName}</span>
              <span className={`text-[9px] px-1 py-px rounded border font-medium shrink-0 ml-1 ${
                t.tableType === 'data'
                  ? 'bg-blue-50 text-blue-500 border-blue-100'
                  : 'bg-purple-50 text-purple-500 border-purple-100'
              }`}>{t.tableType}</span>
            </div>

            {t.tableType === 'data' && t.columns && t.columns.length > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-1">
                {t.columns.slice(0, 6).map(c => (
                  <span key={c.id} className={`text-[9px] font-mono px-1 py-px rounded ${
                    c.isRelation
                      ? 'bg-amber-50 text-amber-600 border border-amber-100'
                      : 'bg-paper-sunken text-ink-dim'
                  }`}>
                    {c.columnName}{c.isRelation && c.relationTargetTable ? ' →' : ''}
                  </span>
                ))}
                {t.columns.length > 6 && (
                  <span className="text-[9px] text-ink-dim">+{t.columns.length - 6} more</span>
                )}
              </div>
            )}

            {t.tableType === 'relation' && t.links && (
              <div className="mt-1 text-[10px] text-purple-500 font-mono">
                {t.links.find(l => l.position === 1)?.linkedTableName ?? '?'}
                {' ↔ '}
                {t.links.find(l => l.position === 2)?.linkedTableName ?? '?'}
              </div>
            )}

            {t.tableType === 'data' && t.idColumn && (
              <div className="mt-1 text-[9px] text-ink-dim">
                id: <span className="font-mono text-ink-dim">{t.idColumn}</span>
              </div>
            )}
          </div>
          )
        })}
      </div>

      {/* Destinations column */}
      <div className="flex flex-col gap-3 flex-none min-w-[170px]">
        <div className="text-[10px] font-semibold text-ink-dim uppercase tracking-widest mb-1">Destination</div>

        {cups.map(cup => (
          <div
            key={cup.key}
            ref={el => { if (el) cupRefs.current.set(cup.key, el); else cupRefs.current.delete(cup.key) }}
            className={`${nodeBase} border-emerald-300 ${nodeRing(cup.key)}`}
            onClick={() => toggle(cup.key)}
          >
            <div className="flex items-center gap-1.5">
              <Package size={11} className="text-emerald-500 shrink-0" />
              <span className="font-mono text-sm font-medium text-emerald-800 truncate">{cup.name}</span>
            </div>
            <div className="text-[9px] text-emerald-400 mt-0.5 uppercase tracking-wide">cup</div>
          </div>
        ))}

        {relTables.map(t => (
          <div
            key={`rel-dst-${t.id}`}
            ref={el => { if (el) relRefs.current.set(t.id, el); else relRefs.current.delete(t.id) }}
            className={`${nodeBase} border-purple-300 ${nodeRing('reldst:' + t.id)}`}
            onClick={() => toggle('reldst:' + t.id)}
          >
            <div className="flex items-center gap-1.5">
              <Link2 size={11} className="text-purple-400 shrink-0" />
              <span className="font-mono text-theme-xs font-medium text-purple-800 truncate">
                {t.links?.find(l => l.position === 1)?.linkedTableName ?? '?'}
                {' ↔ '}
                {t.links?.find(l => l.position === 2)?.linkedTableName ?? '?'}
              </span>
            </div>
            <div className="text-[9px] text-purple-400 mt-0.5 uppercase tracking-wide">relation</div>
          </div>
        ))}

        {cups.length === 0 && relTables.length === 0 && (
          <div className="text-sm text-ink-dim border border-dashed border-paper-border rounded-lg p-6 text-center">
            No destinations mapped
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="absolute bottom-4 right-6 flex items-center gap-4">
        {[
          { color: '#d0d5dd', label: 'Ingestion' },
          { color: '#12b76a', label: 'Mapping' },
          { color: '#a855f7', label: 'Relation', dashed: true },
        ].map(({ color, label, dashed }) => (
          <span key={label} className="flex items-center gap-1.5 text-[10px] text-ink-dim">
            <svg width={24} height={10}>
              <line x1={0} y1={5} x2={24} y2={5} stroke={color} strokeWidth={1.5}
                    strokeDasharray={dashed ? '4 2' : undefined} />
            </svg>
            {label}
          </span>
        ))}
      </div>
    </div>
  )
}

// ── Runs tab ──────────────────────────────────────────────────────────────────

function RunsTab({
  runs,
  onLogsOpen,
  onRefresh,
  onStop,
  stopping,
  onDelete,
  deleting,
}: {
  runs: FunnelRun[]
  onLogsOpen: (id: string) => void
  onRefresh: () => void
  onStop: (runId: string) => void
  stopping: boolean
  onDelete: (runId: string) => void
  deleting: string | null
}) {
  const fmt = useDateFmt()
  const lastError = runs.find(r => r.status === 'failed')?.errorMessage

  return (
    <div>
      {/* Chart section */}
      <div className="border-b border-paper-border pb-5 mb-1">
        <div className="flex items-center justify-between mb-3">
          <span className="text-theme-xs font-semibold text-ink-dim uppercase tracking-widest">
            Run duration — last {Math.min(runs.length, 40)} runs
          </span>
          <button
            onClick={onRefresh}
            className="text-ink-dim hover:text-ink transition-colors p-1 rounded"
            title="Refresh"
          >
            <RefreshCw size={12} />
          </button>
        </div>

        {runs.length === 0 ? (
          <div className="flex items-center justify-center py-12 text-sm text-ink-dim border border-dashed border-paper-border rounded-lg">
            No runs yet — click "Run now" to start ingestion
          </div>
        ) : (
          <RunsChart runs={runs} />
        )}
      </div>

      {/* Error banner */}
      {lastError && (
        <div className="mb-3 text-xs text-red-600 bg-red-50 border border-red-100 rounded px-3 py-2">
          Last error: {lastError}
        </div>
      )}

      {/* Runs table */}
      {runs.length > 0 && (
        <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th>Status</th>
              <th>Started</th>
              <th>Duration</th>
              <th className="text-right">Processed</th>
              <th className="text-right text-emerald-600">Created</th>
              <th className="text-right text-amber-600">Updated</th>
              <th className="text-right text-red-600">Failed</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {runs.map(run => (
              <tr key={run.id}>
                <td><StatusBadge status={run.status} /></td>
                <td className="text-ink-dim text-xs">{fmt(run.startedAt)}</td>
                <td className="text-ink-dim text-xs font-mono">{fmtDuration(run)}</td>
                <td className="text-xs text-right">{run.rowsProcessed.toLocaleString()}</td>
                <td className="text-emerald-600 text-xs text-right">+{run.rowsCreated.toLocaleString()}</td>
                <td className="text-amber-600 text-xs text-right">~{run.rowsUpdated.toLocaleString()}</td>
                <td className={`text-xs text-right ${run.rowsFailed > 0 ? 'text-red-600 font-medium' : 'text-ink-dim'}`}>
                  {run.rowsFailed > 0 ? run.rowsFailed.toLocaleString() : '—'}
                </td>
                <td className="text-right">
                  <div className="flex items-center justify-end gap-2">
                    {run.status === 'running' && (
                      <button
                        onClick={() => onStop(run.id)}
                        disabled={stopping}
                        className="flex items-center gap-1 text-theme-xs text-red-500 hover:text-red-700 transition-colors disabled:opacity-50"
                        title="Stop this run"
                      >
                        <Square size={10} />
                        Stop
                      </button>
                    )}
                    <button
                      onClick={() => onLogsOpen(run.id)}
                      className="text-theme-xs text-accent hover:underline"
                    >
                      Logs
                    </button>
                    <button
                      onClick={() => onDelete(run.id)}
                      disabled={deleting === run.id || run.status === 'running'}
                      className="p-1 text-ink-dim hover:text-red-500 transition-colors disabled:opacity-30"
                      title="Delete this run"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
    </div>
  )
}

// ── SFTP source tab ───────────────────────────────────────────────────────────

function SftpSourcePanel({ connector, runs }: { connector: FunnelConnector; runs: FunnelRun[] }) {
  const lastRun = runs[0]
  return (
    <div className="max-w-md space-y-4">
      <div className="border border-paper-border rounded-lg p-4 space-y-2.5 text-sm">
        <div className="flex justify-between gap-4">
          <span className="text-ink-dim">Remote path</span>
          <span className="font-mono text-ink truncate">{connector.sftpRemotePath || '—'}</span>
        </div>
        <div className="flex justify-between gap-4">
          <span className="text-ink-dim">File filter</span>
          <span className="font-mono text-ink">{connector.sftpFileTypeFilter || 'All files'}</span>
        </div>
        <div className="flex justify-between gap-4">
          <span className="text-ink-dim">Authentication</span>
          <span className="text-ink">{connector.sftpAuthType === 'key' ? 'SSH private key' : 'Password'}</span>
        </div>
      </div>

      {lastRun && (
        <div className="border border-paper-border rounded-lg p-4 space-y-2.5 text-sm">
          <div className="text-theme-xs font-semibold text-ink-dim uppercase tracking-widest mb-1">
            Last sync ({new Date(lastRun.startedAt).toLocaleString()})
          </div>
          <div className="flex justify-between">
            <span className="text-ink-dim">Uploaded</span>
            <span className="text-emerald-600 font-medium">+{lastRun.rowsCreated.toLocaleString()}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-ink-dim">Unchanged</span>
            <span className="text-amber-600">{lastRun.rowsUpdated.toLocaleString()}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-ink-dim">Failed</span>
            <span className={lastRun.rowsFailed > 0 ? 'text-red-600 font-medium' : 'text-ink-dim'}>
              {lastRun.rowsFailed.toLocaleString()}
            </span>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

type Tab = 'pipeline' | 'runs' | 'source'

export function BrainerFunnelDetail() {
  const { id }    = useParams<{ id: string }>()
  const navigate  = useNavigate()
  const qc        = useQueryClient()
  const [tab, setTab]           = useState<Tab>('pipeline')
  const [logsRunId, setLogsRunId] = useState<string | null>(null)

  const { data: connector, isLoading } = useQuery({
    queryKey: ['funnel-connector', id],
    queryFn:  () => getFunnelConnector(id!).then(r => r.data),
    enabled:  !!id,
  })

  const { data: runs = [] } = useQuery({
    queryKey: ['funnel-runs', id],
    queryFn:  () => getFunnelRuns(id!).then(r => r.data),
    enabled:  !!id,
    refetchInterval: (data: any) => {
      const list = (data?.state?.data ?? []) as FunnelRun[]
      return list.some(r => r.status === 'running') ? 3000 : false
    },
  })

  const runMut = useMutation({
    mutationFn: () => startFunnelRun(id!),
    onSuccess:  () => { qc.invalidateQueries({ queryKey: ['funnel-runs', id] }) },
  })

  const stopMut = useMutation({
    mutationFn: (runId: string) => stopFunnelRun(runId),
    onSuccess:  () => { qc.invalidateQueries({ queryKey: ['funnel-runs', id] }) },
  })

  const [deletingRunId, setDeletingRunId] = useState<string | null>(null)
  const deleteMut = useMutation({
    mutationFn: (runId: string) => { setDeletingRunId(runId); return deleteFunnelRun(runId) },
    onSuccess:  () => { setDeletingRunId(null); qc.invalidateQueries({ queryKey: ['funnel-runs', id] }) },
    onError:    () => setDeletingRunId(null),
  })

  const [showDeleteConnector, setShowDeleteConnector] = useState(false)
  const deleteConnectorMut = useMutation({
    mutationFn: () => deleteFunnelConnector(id!),
    onSuccess:  () => navigate('/brainer/funnels'),
  })

  const runList    = (runs as FunnelRun[])
  const runningRun = runList.find(r => r.status === 'running')

  const { data: runningLogs = [] } = useQuery({
    queryKey: ['funnel-run-logs', runningRun?.id ?? ''],
    queryFn:  () => getFunnelRunLogs(runningRun!.id).then(r => r.data),
    enabled:  !!runningRun,
    refetchInterval: 2000,
  })

  const runState = useMemo((): RunState => {
    if (!runningRun) return { isRunning: false, done: 0, total: 0, pct: 0, activeTable: null, phase: null }
    const logs = runningLogs as FunnelRunLog[]
    const dataTables = (connector?.tables ?? []).filter(t => t.tableType === 'data')
    const total = dataTables.length
    const done  = logs.filter(l => l.message.startsWith('Table "') && l.message.includes(': processed')).length
    const lastProcessing = [...logs].reverse().find(l => l.message.startsWith('Processing table "'))
    const activeTable = lastProcessing
      ? (lastProcessing.message.match(/Processing table "([^"]+)"/) ?? [])[1] ?? null
      : null
    const lastPhase = [...logs].reverse().find(l => l.message.startsWith('Phase '))
    const phase = lastPhase?.message ?? null
    const pct = total === 0 ? 0 : Math.min(100, Math.round((done / total) * 100))
    return { isRunning: true, done, total, pct, activeTable, phase }
  }, [runningRun, runningLogs, connector?.tables])

  if (isLoading) return <div className="py-16 text-center text-sm text-ink-dim">Loading…</div>
  if (!connector) return <div className="py-16 text-center text-sm text-red-500">Connector not found</div>

  const hasRunning = !!runningRun
  const isSftp = connector.connectorType === 'sftp'
  const tabs: Tab[] = isSftp ? ['source', 'runs'] : ['pipeline', 'runs']
  const activeTab = tabs.includes(tab) ? tab : tabs[0]

  return (
    <div>

      {/* ── Breadcrumb ── */}
      <div className="flex items-center gap-1.5 mb-3 text-sm">
        <button onClick={() => navigate('/brainer/funnels')}
                className="text-ink-dim hover:text-ink transition-colors">
          Connectors
        </button>
        <ChevronRight size={13} className="text-ink-dim" />
        <span className="text-ink font-medium">{connector.title}</span>
      </div>

      {/* ── Header ── */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-lg font-semibold text-ink">{connector.title}</h1>
            <span className="px-1.5 py-px text-[10px] font-medium rounded border bg-paper-sunken text-ink-dim border-paper-border">
              {connector.connectorType}
            </span>
            {hasRunning && (
              <span className="px-1.5 py-px text-[10px] font-medium rounded border bg-blue-50 text-blue-600 border-blue-100 animate-pulse">
                running
              </span>
            )}
          </div>
          <p className="text-xs text-ink-dim font-mono mt-0.5">
            {isSftp
              ? `${connector.username}@${connector.host}:${connector.port}${connector.sftpRemotePath ?? ''}`
              : `${connector.username}@${connector.host}:${connector.port}/${connector.databaseName}`}
          </p>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <Button variant="danger" icon={Trash2} onClick={() => setShowDeleteConnector(true)} title="Delete connector">
            Delete
          </Button>
          <Button variant="secondary" icon={Settings2} onClick={() => navigate(`/brainer/funnels/${id}/edit`)} title="Edit configuration">
            Configure
          </Button>
          {hasRunning && (
            <Button
              variant="danger"
              size="sm"
              icon={Square}
              onClick={() => stopMut.mutate(runningRun!.id)}
              disabled={stopMut.isPending}
            >
              Stop
            </Button>
          )}
          <Button icon={Play} onClick={() => runMut.mutate()} disabled={runMut.isPending || hasRunning}>
            Run now
          </Button>
        </div>
      </div>

      {/* ── Tab bar ── */}
      <div className="border-b border-paper-border mb-5">
        <nav className="flex -mb-px">
          {tabs.map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors capitalize ${
                activeTab === t
                  ? 'border-brand-500 text-brand-500'
                  : 'border-transparent text-gray-500 hover:text-gray-700-dim hover:text-ink'
              }`}
            >
              {t}
              {t === 'runs' && runList.length > 0 && (
                <span className="ml-1.5 text-[10px] bg-paper-sunken text-ink-dim px-1.5 py-0.5 rounded-full">
                  {runList.length}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* ── Tab content ── */}
      {activeTab === 'pipeline' ? (
        <PipelineGraph connector={connector} runState={runState} />
      ) : activeTab === 'source' ? (
        <SftpSourcePanel connector={connector} runs={runList} />
      ) : (
        <RunsTab
          runs={runList}
          onLogsOpen={setLogsRunId}
          onRefresh={() => qc.invalidateQueries({ queryKey: ['funnel-runs', id] })}
          onStop={(runId) => stopMut.mutate(runId)}
          stopping={stopMut.isPending}
          onDelete={(runId) => deleteMut.mutate(runId)}
          deleting={deletingRunId}
        />
      )}

      {logsRunId && <LogsModal runId={logsRunId} onClose={() => setLogsRunId(null)} />}

      {showDeleteConnector && (
        <Modal title="Delete connector" onClose={() => setShowDeleteConnector(false)} size="sm">
          <p className="text-sm text-ink-dim mb-4">
            Delete <strong>{connector.title}</strong>? This will also remove all ingestion history and row maps. This cannot be undone.
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setShowDeleteConnector(false)}>Cancel</Button>
            <Button
              variant="danger"
              onClick={() => deleteConnectorMut.mutate()}
              disabled={deleteConnectorMut.isPending}
              loading={deleteConnectorMut.isPending}
            >
              Delete
            </Button>
          </div>
        </Modal>
      )}
    </div>
  )
}
